import React, { useCallback, useEffect, useState } from 'react';
import { getExpiredProductOptionsCount } from '../../services/ClientService';

const POLL_MS = 60 * 1000;

/**
 * Hook: expired product-option count for Product Expiry sidebar badge.
 */
export const useHeaderExpiredProductCount = () => {
    const [expiredCount, setExpiredCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const refresh = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await getExpiredProductOptionsCount({});
            if (res?.success) {
                setExpiredCount(Number(res.data?.count) || 0);
            }
        } catch {
            /* keep last known count */
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (cancelled) return;
            await refresh();
        };
        run();
        const id = setInterval(run, POLL_MS);

        const onVisible = () => {
            if (document.visibilityState === 'visible') run();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        window.addEventListener('product-expiry-updated', onVisible);

        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
            window.removeEventListener('product-expiry-updated', onVisible);
        };
    }, [refresh]);

    return { expiredCount, isLoading, refresh };
};

/**
 * Compact badge on the Product Expiry icon (collapsed sidebar / icon-only).
 */
const HeaderExpiryBadge = ({ count, className = '' }) => {
    if (!count || count === 0) return null;

    return (
        <span
            className={`
                absolute -top-1 -right-1
                min-w-[18px] h-[18px]
                flex items-center justify-center
                bg-amber-500 text-white
                text-[10px] font-bold
                rounded-full
                px-1
                shadow-sm
                ring-2 ring-white
                ${className}
            `}
            title={`${count} expired product option${count === 1 ? '' : 's'}`}
        >
            {count > 99 ? '99+' : count}
        </span>
    );
};

export default HeaderExpiryBadge;
